import { useState, useEffect, Fragment } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import LanguageDropdown from '../../UI/LanguageDropdown';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import PasswordComponent from '../../UI/PasswordComponent';
import SuccessBox from '../../UI/SuccessBox';
import HtmlInput from '../../UI/HtmlInput';
import useAnalyticsEventTracker from '../../../hooks/use-analytics';
import EmailComponent from '../../UI/EmailComponent';
import Checkbox from '../../UI/Checkbox';
import useFbPixel from '../../../hooks/use-fb-pixel';
import { getLangFromCode } from '../../Helpers/Util/User';
import parrotBitesize from '../../../assets/images/parrot-cutout-bitesize.png';

const SignUp = ({ registerLang, originalReferer }) => {
  const { t: translate } = useTranslation();
  const [updateNative, setUpdateNative] = useState(false);
  let browserLang = navigator.language
  browserLang = browserLang.includes('-') ? browserLang.split('-')[0] : browserLang;
  let nativeLangCode = browserLang !== '' ? browserLang : 'en';
  let nativeLang = getLangFromCode(nativeLangCode)

  const formFields = {
    firstname: '',
    lastname: '',
    nickname: '',
    email: '',
    emailConfirm: '',
    password: '',
    repeatPassword: '',
    learning: registerLang
      ? translate(registerLang)
      : translate('select-language'),
    language: nativeLang,
    request_languages: '',
    termsconditions: false,
  };
  const history = useHistory();
  const [values, setValues] = useState(formFields);
  const [errors, setErrors] = useState({});
  const [signUpErrors, setSignUpErrors] = useState('');
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { apiUrl } = window['appConfig'];
  const gaEventTracker = useAnalyticsEventTracker('Registration');
  const sendFbEvent = useFbPixel();

  const handleChange = (e) => {
    let { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      value = checked;
    }

    setValues({
      ...values,
      [name]: value,
    });
  };

  const handleDropdownChange = (name, value) => {
    setValues({
      ...values,
      [name]: value,
    });
  };

  const handleLangRequestBlur = (e) => {
    // eslint-disable-next-line no-use-before-define
    const { value } = e.target;
    gaEventTracker('languages-requested', value);
  };

  const populateNickname = () => {
    setValues({
      ...values,
      ['nickname']: values.firstname,
    });
  }

  const handleSignup = (e) => {
    e.preventDefault();
    setErrors({});
    //Error Checks
    if (!values.firstname) {
      setErrors((prevState) => ({
        ...prevState,
        firstname: translate('firstname-required'),
      }));
    }

    if (!values.lastname) {
      setErrors((prevState) => ({
        ...prevState,
        lastname: translate('surname-required'),
      }));
    }

    if (!values.nickname) {
      setErrors((prevState) => ({
        ...prevState,
        nickname: translate('nickname-required'),
      }));
    }

    if (!values.email) {
      setErrors((prevState) => ({
        ...prevState,
        email: translate('email-required'),
      }));
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
      setErrors((prevState) => ({
        ...prevState,
        email: translate('email-invalid'),
      }));
    }

    if (!values.emailConfirm) {
      setErrors((prevState) => ({
        ...prevState,
        emailConfirm: translate('confirm-email'),
      }));
    }

    if (!values.password) {
      setErrors((prevState) => ({
        ...prevState,
        password: translate('password-required'),
      }));
    }

    if (!values.repeatPassword) {
      setErrors((prevState) => ({
        ...prevState,
        repeatPassword: translate('confirm-password'),
      }));
    }

    if (values.learning === translate('select-language')) {
      setErrors((prevState) => ({
        ...prevState,
        learning: translate('select-learning-language'),
      }));
    }

    if (values.language === translate('select-language')) {
      setErrors((prevState) => ({
        ...prevState,
        language: translate('select-native-language'),
      }));
    }

    if (!values.termsconditions) {
      setErrors((prevState) => ({
        ...prevState,
        termsconditions: translate('agree-tcs'),
      }));
    }

    //Stop form being tampered with during submition
    setIsSubmitting(true);
  };

  const submitSignup = async () => {
    const formData = new URLSearchParams();

    for (const property in values) {
      formData.append(property, values[property]);
    }

    const referer =
      originalReferer || !document.referrer ? '' : document.referrer;
    const response = await fetch(`${apiUrl}/user/register`, {
      headers: {
        'X-Original-Referer': `${referer}`,
      },
      method: 'POST',
      body: formData,
    });

    if (response.ok) {
      // only send to FB if success on sign up
      sendFbEvent('sign-up-clicked-success', formFields?.email);
    }

    const data = await response.json();

    return data;
  };

  const confirmEmail = () => {
    history.push(`/confirm-email?email=${encodeURIComponent(values.email)}`);
    //reset form values
    setValues(formFields);
  };

  let form = (
    <Fragment>
      <p className="intro-text">{translate('signup-welcome')}</p>
      <form className={`signup`} onSubmit={handleSignup}>
        {isSubmitting && <div className="disabled-overlay"></div>}
        <div
          className={`input-row input-row--half ${errors.firstname ? 'input-row--error' : ''
            }`}>
          <HtmlInput
            id="firstname"
            name="firstname"
            legend={translate('firstname')}
            value={values.firstname}
            changeCallback={handleChange}
            hasErrors={'firstname' in errors}
            blurCallback={populateNickname}
          />
          <small className='input-row__description'>*Will be shown in conversations</small>
          {errors.firstname && (
            <p className="input-row__error">{errors.firstname}</p>
          )}
        </div>
        <div
          className={`input-row input-row--half ${errors.lastname ? 'input-row--error' : ''
            }`}>
          <HtmlInput
            id="lastname"
            name="lastname"
            legend={translate('surname')}
            value={values.lastname}
            changeCallback={handleChange}
            hasErrors={'lastname' in errors}
          />
          {errors.lastname && (
            <p className="input-row__error">{errors.lastname}</p>
          )}
        </div>
        <EmailComponent
          formErrors={errors}
          setFormErrors={setErrors}
          setFormValues={setValues}
        />
        <PasswordComponent
          setFormErrors={setErrors}
          setFormValues={setValues}
        />
        <div
          className={`input-row ${errors.learning ? 'input-row--error' : ''
            }`}>
          <label htmlFor="learning">{translate('select-learning-language')}</label>
          <LanguageDropdown
            onClickHandler={handleDropdownChange}
            name={'learning'}
            position={'bottom'}
            defaultValue={formFields.learning}
            allowGA={true}
          />
          {errors.learning && (
            <p className="input-row__error">{errors.learning}</p>
          )}
        </div>
        <div
          className={`input-row ${errors.language ? 'input-row--error' : ''
            }`}>
          {!updateNative && 
            <div className='parrot-box parrot-box--bitesize'>
              <img src={parrotBitesize} alt="golingo Parrot" />
              <div className='parrot-box__text'>
                {translate('detected-lang', {nativeLang: translate(nativeLang)})} <span className="change-lang" onClick={() => {setUpdateNative(!updateNative)}}>{translate('click-here-change')}</span>
              </div>
            </div>}
          {updateNative && <Fragment>
            <label htmlFor="language">{translate('select-native-language')}</label>
            <LanguageDropdown
              onClickHandler={handleDropdownChange}
              name={'language'}
              position={'bottom'}
              defaultValue={formFields.language}
              allowGA={true}
              onlyShowNative={true}
            />
            <p><span className="change-lang" onClick={() => {setUpdateNative(!updateNative)}}>Cancel</span></p>
            {errors.language && (
              <p className="input-row__error">{errors.language}</p>
            )}
          </Fragment>
          }
        </div>
        <div className={`input-row`}>
          <HtmlInput
            id="request_languages"
            name="request_languages"
            legend={translate('request-learning-lang')}
            value={values.request_languages}
            changeCallback={handleChange}
            hasErrors={false}
            blurCallback={handleLangRequestBlur}
          />
        </div>
        <div className={`input-row input-row--centre`}>
          <Checkbox
            id="termsconditions"
            name="termsconditions"
            label={translate('tcs-text')}
            labelSize="0.875"
            hasErrors={'termsconditions' in errors}
            changeCallback={handleChange}
          />
          {errors.termsconditions && (
            <p className="input-row__error">{errors.termsconditions}</p>
          )}
        </div>
        {signUpErrors && <p className="error-field">{signUpErrors}</p>}
        <button
          className={`button landing-button button--success ${isSubmitting ? 'button--loading' : ''
            }`}
          disabled={isSubmitting ? true : false}
          onClick={() => {
            gaEventTracker('sign-up-clicked');
          }}>
          <span>{translate('sign-up')}</span>
          <FontAwesomeIcon icon={faChevronRight} size="1x" />
        </button>
      </form>
    </Fragment>
  );

  let successBox = (
    <SuccessBox
      title={translate('signup-successful')}
      callback={confirmEmail}
    />
  );

  const onRegistrationPageLoaded = () => {
    gaEventTracker('registration-page-loaded');
    sendFbEvent('registration-page-loaded');

    const referer = originalReferer || !document.referrer ? '' : document.referrer;

    const endpointOptions = {
      method: 'POST',
      headers: {
        'X-Original-Referer': referer,
      },
    };
    const endpointUrl = `${apiUrl}/event/regpageloaded`;
    fetch(endpointUrl, endpointOptions)
  };

  useEffect(() => {
    onRegistrationPageLoaded();
  }, []);

  useEffect(() => {
    if (Object.keys(errors).length === 0 && isSubmitting) {
      gaEventTracker('sign-up-clicked');
      submitSignup()
        .then((data) => {
          setIsSubmitting(false);
          if (data.success !== true) {
            gaEventTracker('sign-up-clicked-failed', data.error);
            setSignUpErrors(data.error);
            return;
          }
          gaEventTracker('sign-up-clicked-success');
          setSignupSuccess(true);
        })
        .catch((error) => {
          gaEventTracker('sign-up-clicked-failed', error.error);
          setIsSubmitting(false);
          setSignUpErrors(error.error);
        });
    } else {
      setIsSubmitting(false);
    }
  }, [errors]);

  return <Fragment>{signupSuccess === true ? successBox : form}</Fragment>;
};

export default SignUp;
